import React, { useState, useEffect } from 'react';
import { Configuration, Deal, SystemInfoResponse, GenerationStats } from './types';
import { generateDeals, redealDeal, getSystemInfo } from './services/api';
import { 
  saveCurrentConfiguration, 
  loadCurrentConfiguration,
  saveDeals,
  loadDeals,
  exportConfiguration,
  importConfiguration
} from './utils/storage';
import { exportToPBN, exportToBRI, exportToHTML, downloadFile } from './utils/exportFormats';
import { SAMPLE_SETS, SampleSet } from './samples';
import './App.css';

// Function to create a fresh default configuration
const createDefaultConfig = (): Configuration => ({
  totalDeals: 28,
  biddingSystem: 'NZ_Acol',
  categories: [
    {
      name: 'Random Hands',
      weight: 10,
      setDealer: 'None',
      openingDirection: 'Any',
      criteria: {
        opener: {
          allowRuleOf20: true,
          openingBids: ['DontCare']
        }
      }
    }
  ]
});

// Default configuration (for backward compatibility)
const defaultConfig = createDefaultConfig();

function App() {
  const [configuration, setConfiguration] = useState<Configuration>(defaultConfig);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfoResponse | null>(null);
  const [generationStats, setGenerationStats] = useState<GenerationStats | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'deals' | 'stats' | 'help'>('config');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [activeCriteriaTabs, setActiveCriteriaTabs] = useState<{ [key: number]: string }>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [showSamples, setShowSamples] = useState(false);
  const [expandedSampleCategories, setExpandedSampleCategories] = useState<Set<string>>(new Set());
  const [redealingDealNumber, setRedealingDealNumber] = useState<number | null>(null);

  // Helper function to update category criteria safely
  const updateCategoryCriteria = (categoryIndex: number, updates: Partial<Configuration['categories'][0]>) => {
    const newCategories = configuration.categories.map((cat, i) => 
      i === categoryIndex ? { ...cat, ...updates } : cat
    );
    setConfiguration({ ...configuration, categories: newCategories });
  };

  // Helper function to update opener criteria safely
  const updateOpenerCriteria = (categoryIndex: number, updates: Partial<Configuration['categories'][0]['criteria']['opener']>) => {
    const newCategories = configuration.categories.map((cat, i) => {
      if (i !== categoryIndex) return cat;
      
      const newCat = { ...cat };
      if (!newCat.criteria.opener) {
        newCat.criteria.opener = {
          hcpMin: undefined,
          hcpMax: undefined,
          openingBids: [],
          allowRuleOf20: true,
          balanced: undefined,
          longestSuitMin: undefined,
          longestSuitMax: undefined,
          fourCardMajor: undefined
        };
      } else {
        newCat.criteria.opener = { ...newCat.criteria.opener };
      }
      
      Object.assign(newCat.criteria.opener, updates);
      return newCat;
    });
    setConfiguration({ ...configuration, categories: newCategories });
  };

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Scroll to top when switching to deals tab
  useEffect(() => {
    if (activeTab === 'deals') {
      window.scrollTo(0, 0);
      // Reset selected deal when switching to deals tab
      setSelectedDeal(null);
    }
  }, [activeTab]);

  // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeTooltip && !(event.target as Element).closest('.tooltip') && !(event.target as Element).closest('.help-icon')) {
        setActiveTooltip(null);
      }
    };

    // Use mousedown instead of click to avoid conflict with help icon clicks
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeTooltip]);

  const toggleCategoryExpanded = (index: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCategories(newExpanded);
  };

  const setActiveCriteriaTab = (categoryIndex: number, tabName: string) => {
    setActiveCriteriaTabs(prev => ({
      ...prev,
      [categoryIndex]: tabName
    }));
  };

  const loadInitialData = async () => {
    try {
      // Load saved configuration
      const savedConfig = loadCurrentConfiguration();
      if (savedConfig) {
        setConfiguration(savedConfig);
        
        // Expand the first category if there are categories
        if (savedConfig.categories.length > 0) {
          setExpandedCategories(new Set([0]));
        }
      } else {
        // If no saved config, expand the first category of the default config
        if (defaultConfig.categories.length > 0) {
          setExpandedCategories(new Set([0]));
        }
      }

      // Load saved deals
      const savedDeals = loadDeals();
      if (savedDeals.length > 0) {
        setDeals(savedDeals);
      }

      // Get system info
      const info = await getSystemInfo();
      setSystemInfo(info);
    } catch (err) {
      setError('Failed to load initial data');
    }
  };

  const handleGenerateDeals = async () => {
    setLoading(true);
    setError(null);
    setGenerationStats(null);

    try {
      // Validate configuration before sending
      const validatedConfig = { ...configuration };
      
      // Ensure all categories have proper opener criteria
      validatedConfig.categories = validatedConfig.categories.map(category => {
        if (!category.criteria.opener) {
          return {
            ...category,
            criteria: {
              ...category.criteria,
              opener: {
                allowRuleOf20: true,
                openingBids: ['DontCare']
              }
            }
          };
        }
        return category;
      });

      // Save current configuration
      saveCurrentConfiguration(validatedConfig);

      // Generate deals
      const response = await generateDeals(validatedConfig);
      setDeals(response.deals);
      setGenerationStats(response.stats || null);
      
      // Save deals
      saveDeals(response.deals);
      
      setActiveTab('deals');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate deals');
    } finally {
      setLoading(false);
    }
  };

  const handleDealReorder = (fromIndex: number, toIndex: number) => {
    const newDeals = [...deals];
    const [movedDeal] = newDeals.splice(fromIndex, 1);
    newDeals.splice(toIndex, 0, movedDeal);
    
    // Update deal numbers, dealer, and vulnerability based on new positions
    newDeals.forEach((deal, index) => {
      deal.dealNumber = index + 1;
      
      // Assign dealer (N, E, S, W repeating)
      const positions = ['North', 'East', 'South', 'West'];
      deal.dealer = positions[index % 4];
      
      // Assign vulnerability (16-board pattern)
      const vulnerabilityPattern = [
        'None', 'NS', 'EW', 'Both',
        'NS', 'EW', 'Both', 'None',
        'None', 'EW', 'Both', 'NS',
        'Both', 'None', 'NS', 'EW',
      ];
      deal.vulnerability = vulnerabilityPattern[index % 16];
    });
    
    setDeals(newDeals);
    saveDeals(newDeals);
  };

  const handleDealRotation = (dealIndex: number, direction: 'clockwise' | 'counterclockwise') => {
    const newDeals = [...deals];
    const deal = { ...newDeals[dealIndex] };
    
    if (direction === 'clockwise') {
      // Rotate hands clockwise
      const temp = deal.north;
      deal.north = deal.west;
      deal.west = deal.south;
      deal.south = deal.east;
      deal.east = temp;
      
      // Update opening hand (dealer stays the same)
      const positions = ['North', 'East', 'South', 'West'];
      const openingIndex = positions.indexOf(deal.openingHand);
      deal.openingHand = positions[(openingIndex + 1) % 4];
    } else {
      // Rotate hands counterclockwise
      const temp = deal.north;
      deal.north = deal.east;
      deal.east = deal.south;
      deal.south = deal.west;
      deal.west = temp;
      
      // Update opening hand (dealer stays the same)
      const positions = ['North', 'East', 'South', 'West'];
      const openingIndex = positions.indexOf(deal.openingHand);
      deal.openingHand = positions[(openingIndex + 3) % 4];
    }
    
    newDeals[dealIndex] = deal;
    setDeals(newDeals);
    saveDeals(newDeals);
    if (selectedDeal?.dealNumber === deal.dealNumber) {
      setSelectedDeal(deal);
    }
  };

  const handleRedeal = async (dealIndex: number) => {
    const existing = deals[dealIndex];
    if (!existing) return;

    const category = configuration.categories.find((c) => c.name === existing.category);
    if (!category) {
      setError(
        `Cannot re-deal board ${existing.dealNumber}: category "${existing.category}" is not in the current configuration.`
      );
      return;
    }

    setRedealingDealNumber(existing.dealNumber);
    setError(null);

    try {
      const response = await redealDeal({
        biddingSystem: configuration.biddingSystem,
        category,
        dealNumber: existing.dealNumber,
        dealer: existing.dealer,
        vulnerability: existing.vulnerability,
      });

      const newDeals = [...deals];
      newDeals[dealIndex] = response.deal;
      setDeals(newDeals);
      saveDeals(newDeals);

      if (selectedDeal?.dealNumber === existing.dealNumber) {
        setSelectedDeal(response.deal);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to re-deal board ${existing.dealNumber}`);
    } finally {
      setRedealingDealNumber(null);
    }
  };

  const handleExport = (format: 'PBN' | 'BRI' | 'HTML') => {
    if (deals.length === 0) {
      setError('No deals to export');
      return;
    }

    try {
      let content: string | Uint8Array;
      let filename: string;
      let mimeType: string;

      switch (format) {
        case 'PBN':
          content = exportToPBN(deals);
          filename = `bridge_deals_${new Date().toISOString().split('T')[0]}.pbn`;
          mimeType = 'text/plain';
          break;
        case 'BRI':
          content = exportToBRI(deals);
          filename = `bridge_deals_${new Date().toISOString().split('T')[0]}.bri`;
          mimeType = 'application/octet-stream';
          break;
        case 'HTML':
          content = exportToHTML(deals);
          filename = `bridge_deals_${new Date().toISOString().split('T')[0]}.html`;
          mimeType = 'text/html';
          break;
      }

      downloadFile(content, filename, mimeType);
    } catch (err) {
      setError('Failed to export deals');
    }
  };

  const handleConfigExport = () => {
    try {
      exportConfiguration(configuration, `bridge_config_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError('Failed to export configuration');
    }
  };

  const handleConfigImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    importConfiguration(file)
      .then((config) => {
        setConfiguration(config);
        saveCurrentConfiguration(config);
        setError(null);
      })
      .catch((err) => {
        setError('Failed to import configuration: ' + err.message);
      });
  };

  const handleLoadSampleSet = (sample: SampleSet) => {
    const sampleConfig: Configuration = JSON.parse(JSON.stringify(sample.config));
    // Keep the user's chosen deal count; samples define category weights, not total deals
    sampleConfig.totalDeals = configuration.totalDeals;

    setConfiguration(sampleConfig);
    saveCurrentConfiguration(sampleConfig);

    // Expand all categories so the loaded sample is easy to inspect
    setExpandedCategories(new Set(sampleConfig.categories.map((_, i) => i)));
    setActiveCriteriaTabs({});
    setShowSamples(false);

    setError(null);
    setDeals([]);
    saveDeals([]);
    setGenerationStats(null);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings and clear all generated deals? This action cannot be undone.')) {
      // Create a fresh default configuration
      const freshDefaultConfig = createDefaultConfig();
      
      console.log('Resetting to fresh config:', freshDefaultConfig);

      // Reset configuration to fresh default
      setConfiguration(freshDefaultConfig);
      saveCurrentConfiguration(freshDefaultConfig);

      // Clear all deals
      setDeals([]);
      saveDeals([]);

      // Clear any errors
      setError(null);

      // Reset generation stats
      setGenerationStats(null);

      // Reset UI state
      setExpandedCategories(new Set([0])); // Always expand first category
      setActiveCriteriaTabs({});
      setViewMode('grid');
      setSelectedDeal(null);
    }
  };

  const calculateStatistics = () => {
    if (deals.length === 0) return null;

    // Partnership statistics
    const nsHcps = deals.map(d => d.north.hcp + d.south.hcp);
    const ewHcps = deals.map(d => d.east.hcp + d.west.hcp);
    
    const nsAverageHcp = nsHcps.reduce((sum, hcp) => sum + hcp, 0) / nsHcps.length;
    const ewAverageHcp = ewHcps.reduce((sum, hcp) => sum + hcp, 0) / ewHcps.length;

    // Individual statistics
    const northHcps = deals.map(d => d.north.hcp);
    const southHcps = deals.map(d => d.south.hcp);
    const eastHcps = deals.map(d => d.east.hcp);
    const westHcps = deals.map(d => d.west.hcp);

    const northAverageHcp = northHcps.reduce((sum, hcp) => sum + hcp, 0) / northHcps.length;
    const southAverageHcp = southHcps.reduce((sum, hcp) => sum + hcp, 0) / southHcps.length;
    const eastAverageHcp = eastHcps.reduce((sum, hcp) => sum + hcp, 0) / eastHcps.length;
    const westAverageHcp = westHcps.reduce((sum, hcp) => sum + hcp, 0) / westHcps.length;

    // Opening hand statistics
    const openingHandCounts: { [key: string]: number } = {};
    deals.forEach(deal => {
      openingHandCounts[deal.openingHand] = (openingHandCounts[deal.openingHand] || 0) + 1;
    });

    const openingHandPercentages: { [key: string]: number } = {};
    Object.keys(openingHandCounts).forEach(direction => {
      openingHandPercentages[direction] = (openingHandCounts[direction] / deals.length) * 100;
    });

    return {
      partnershipStats: {
        nsAverageHcp: Math.round(nsAverageHcp * 100) / 100,
        ewAverageHcp: Math.round(ewAverageHcp * 100) / 100,
        nsHcpDistribution: { min: Math.min(...nsHcps), max: Math.max(...nsHcps), stdDev: 0 },
        ewHcpDistribution: { min: Math.min(...ewHcps), max: Math.max(...ewHcps), stdDev: 0 }
      },
      individualStats: {
        northAverageHcp: Math.round(northAverageHcp * 100) / 100,
        southAverageHcp: Math.round(southAverageHcp * 100) / 100,
        eastAverageHcp: Math.round(eastAverageHcp * 100) / 100,
        westAverageHcp: Math.round(westAverageHcp * 100) / 100,
        directionHcpDistribution: {}
      },
      openingHandStats: {
        openingHandCounts,
        openingHandPercentages,
        categoryBreakdown: {}
      }
    };
  };

  const stats = calculateStatistics();

  return (
    <div className="App">
      <header className="App-header">
        <h1>Bridge Deal Generator</h1>
        <div className="tab-navigation">
          <button 
            className={activeTab === 'config' ? 'active' : ''} 
            onClick={() => setActiveTab('config')}
          >
            Configuration
          </button>
          <button 
            className={activeTab === 'deals' ? 'active' : ''} 
            onClick={() => setActiveTab('deals')}
            disabled={deals.length === 0}
          >
            Deals ({deals.length})
          </button>
          <button 
            className={activeTab === 'stats' ? 'active' : ''} 
            onClick={() => setActiveTab('stats')}
            disabled={deals.length === 0}
          >
            Statistics
          </button>
          <button 
            className={activeTab === 'help' ? 'active' : ''} 
            onClick={() => setActiveTab('help')}
          >
            Help
          </button>
        </div>
      </header>

      <main className="App-main">
        {activeTab === 'config' && (
          <div className="config-panel">
            <h2>Configuration</h2>
            
            <div className="config-section">
              <label>
                Total Deals:
                <input
                  type="number"
                  min="1"
                  max={systemInfo?.maxDeals || 1000}
                  value={configuration.totalDeals}
                  onChange={(e) => setConfiguration({
                    ...configuration,
                    totalDeals: parseInt(e.target.value) || 28
                  })}
                />
              </label>
            </div>

            <div className="config-section">
              <label>
                Bidding System:
                <select
                  value={configuration.biddingSystem}
                  onChange={(e) => setConfiguration({
                    ...configuration,
                    biddingSystem: e.target.value
                  })}
                >
                  {systemInfo?.biddingSystems.map(system => (
                    <option key={system} value={system}>{system}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="config-actions">
              <button onClick={handleGenerateDeals} disabled={loading} className="generate-button">
                {loading ? 'Generating...' : 'Generate Deals'}
              </button>
              <button
                onClick={() => setShowSamples((prev) => !prev)}
                className="btn btn-secondary"
                aria-expanded={showSamples}
              >
                {showSamples ? 'Hide Samples' : 'Show Samples'}
              </button>
              <button onClick={handleConfigExport} className="btn btn-secondary">Export Config</button>
              <label className="import-button btn btn-secondary">
                Import Config
                <input
                  type="file"
                  accept=".json"
                  onChange={handleConfigImport}
                  style={{ display: 'none' }}
                />
              </label>
              <button onClick={handleReset} className="reset-button">
                Reset All
              </button>
            </div>

            {showSamples && (
              <div className="samples-panel">
                <div className="samples-panel-header">
                  <h3>Sample configurations</h3>
                  <p>
                    Built-in starting points to explore what the generator can do.
                    Loading a sample replaces your current categories and weights.
                  </p>
                </div>
                <div className="samples-list">
                  {SAMPLE_SETS.map((sample) => (
                    <div key={sample.id} className="sample-item">
                      <div className="sample-item-header">
                        <div className="sample-item-main">
                          <span className="sample-name">{sample.name}</span>
                          <p className="sample-description">{sample.description}</p>
                          <button
                            type="button"
                            className="sample-categories-toggle"
                            aria-expanded={expandedSampleCategories.has(sample.id)}
                            onClick={() => {
                              setExpandedSampleCategories((prev) => {
                                const next = new Set(prev);
                                if (next.has(sample.id)) {
                                  next.delete(sample.id);
                                } else {
                                  next.add(sample.id);
                                }
                                return next;
                              });
                            }}
                          >
                            {expandedSampleCategories.has(sample.id) ? '▼' : '▶'}{' '}
                            {expandedSampleCategories.has(sample.id)
                              ? 'Hide categories'
                              : `Show categories (${sample.config.categories.length})`}
                          </button>
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary sample-load-btn"
                          onClick={() => handleLoadSampleSet(sample)}
                        >
                          Load
                        </button>
                      </div>
                      {expandedSampleCategories.has(sample.id) && (
                        <ul className="sample-category-list">
                          {sample.config.categories.map((category, index) => (
                            <li key={`${sample.id}-${index}`}>
                              <strong>{category.name}</strong>
                              <span>{sample.categoryDescriptions[index]}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
                <button onClick={() => setError(null)}>×</button>
              </div>
            )}

            <div className="config-section">
              <h3>Categories</h3>
              <button 
                className="add-category-btn"
                onClick={() => {
                  const newCategories = [...configuration.categories, {
                    name: '',
                    weight: 10,
                    setDealer: 'None' as const,
                    openingDirection: 'Any' as const,
                    criteria: {
                      opener: {
                        allowRuleOf20: true,
                        openingBids: ['DontCare']
                      }
                    }
                  }];
                  setConfiguration({ ...configuration, categories: newCategories });
                  
                  // Automatically expand the new category
                  const newCategoryIndex = newCategories.length - 1;
                  setExpandedCategories(prev => {
                    const newSet = new Set(prev);
                    newSet.add(newCategoryIndex);
                    return newSet;
                  });
                }}
              >
                + Add Category
              </button>
              
              {configuration.categories.map((category, index) => (
                <div key={index} className="category-item">
                  <div className="category-header">
                    <button 
                      className="expand-toggle"
                      onClick={() => toggleCategoryExpanded(index)}
                    >
                      {expandedCategories.has(index) ? '▼' : '▶'}
                    </button>
                    <div className="category-basic-info">
                      {expandedCategories.has(index) ? (
                        <>
                          <label>Name: <input
                            type="text"
                            placeholder="Category name"
                            value={category.name}
                            onChange={(e) => {
                              updateCategoryCriteria(index, { name: e.target.value });
                            }}
                          /></label>
                          <label className="field-with-help">
                            Weight: 
                            <span 
                              className="help-icon" 
                              onClick={() => setActiveTooltip(`weight-${index}`)}
                              title="Click for help"
                            >
                              ℹ️
                            </span>
                            <input
                              type="number"
                              placeholder="Weight"
                              min="1"
                              value={category.weight}
                                                          onChange={(e) => {
                              updateCategoryCriteria(index, { weight: parseInt(e.target.value) || 1 });
                            }}
                            />
                            {activeTooltip === `weight-${index}` && (
                              <div 
                                className="tooltip"
                                onMouseLeave={() => setActiveTooltip(null)}
                              >
                                Weight determines how often this category appears in generated deals. Higher weight = more frequent.
                              </div>
                            )}
                          </label>
                          <label className="field-with-help">
                            Set Dealer: 
                            <span 
                              className="help-icon" 
                              onClick={() => setActiveTooltip(`dealer-${index}`)}
                              title="Click for help"
                            >
                              ℹ️
                            </span>
                            <select
                              value={category.setDealer}
                              onChange={(e) => {
                                updateCategoryCriteria(index, { setDealer: e.target.value as any });
                              }}
                            >
                              <option value="None">None</option>
                              <option value="Exact">Exact</option>
                              <option value="Close">Close</option>
                            </select>
                            {activeTooltip === `dealer-${index}` && (
                              <div 
                                className="tooltip"
                                onMouseLeave={() => setActiveTooltip(null)}
                              >
                                Set Dealer controls Opening Hand positioning: None=random, Exact=is dealer, Close=is dealer or hand after dealer
                              </div>
                            )}
                          </label>
                        </>
                      ) : (
                        <span className="category-name">{category.name || 'Unnamed Category'}</span>
                      )}
                    </div>
                    <button onClick={() => {
                      const newCategories = configuration.categories.filter((_, i) => i !== index);
                      setConfiguration({ ...configuration, categories: newCategories });
                    }}>
                      Remove
                    </button>
                  </div>
                  
                  {/* Collapsible Category Content */}
                  {expandedCategories.has(index) && (
                    <div className="category-content">
                      {/* Criteria Tabs */}
                      <div className="criteria-tabs">
                        <button 
                          className={`criteria-tab ${(activeCriteriaTabs[index] || 'opener') === 'opener' ? 'active' : ''}`}
                          onClick={() => setActiveCriteriaTab(index, 'opener')}
                        >
                          Opener
                        </button>
                        <button 
                          className={`criteria-tab ${activeCriteriaTabs[index] === 'responder' ? 'active' : ''}`}
                          onClick={() => setActiveCriteriaTab(index, 'responder')}
                        >
                          Responder
                        </button>
                        <button 
                          className={`criteria-tab ${activeCriteriaTabs[index] === 'openingPartnership' ? 'active' : ''}`}
                          onClick={() => setActiveCriteriaTab(index, 'openingPartnership')}
                        >
                          Opening Partnership
                        </button>
                        <button 
                          className={`criteria-tab ${activeCriteriaTabs[index] === 'lhoOvercall' ? 'active' : ''}`}
                          onClick={() => setActiveCriteriaTab(index, 'lhoOvercall')}
                        >
                          Left Hand Opponent
                        </button>
                        <button 
                          className={`criteria-tab ${activeCriteriaTabs[index] === 'opponentPartnership' ? 'active' : ''}`}
                          onClick={() => setActiveCriteriaTab(index, 'opponentPartnership')}
                        >
                          Opponent Partnership
                        </button>
                      </div>

                      {/* Criteria Content */}
                      <div className="criteria-content">
                        {/* Opener Criteria Tab */}
                        {(activeCriteriaTabs[index] || 'opener') === 'opener' && (
                          <div className="criteria-tab-content">
                            <h4>Opener Requirements</h4>
                            <div className="criteria-row">
                              <label className="field-with-help">
                                Set opening direction:
                                <span
                                  className="help-icon"
                                  onClick={() => setActiveTooltip(`opening-dir-${index}`)}
                                  title="Click for help"
                                >
                                  ℹ️
                                </span>
                                <select
                                  value={category.openingDirection || 'Any'}
                                  disabled={category.setDealer !== 'None'}
                                  onChange={(e) => {
                                    updateCategoryCriteria(index, {
                                      openingDirection: e.target.value as any,
                                    });
                                  }}
                                >
                                  <option value="Any">Any</option>
                                  <option value="NS">N or S</option>
                                  <option value="EW">E or W</option>
                                  <option value="North">North</option>
                                  <option value="South">South</option>
                                  <option value="East">East</option>
                                  <option value="West">West</option>
                                </select>
                                {activeTooltip === `opening-dir-${index}` && (
                                  <div
                                    className="tooltip"
                                    onMouseLeave={() => setActiveTooltip(null)}
                                  >
                                    Places the opening hand at a compass seat by rotating the deal
                                    (dealer stays fixed). Only used when Set Dealer is None.
                                    &quot;N or S&quot; / &quot;E or W&quot; keep the opener in that pair,
                                    or pick one at random if it is not already.
                                  </div>
                                )}
                              </label>
                            </div>
                            <div className="criteria-row">
                              <label>HCP Min: <input type="number" min="0" max="40" value={category.criteria.opener?.hcpMin || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opener) {
                                  newCategories[index].criteria.opener = {
                                    hcpMin: undefined,
                                    hcpMax: undefined,
                                    openingBids: [],
                                    allowRuleOf20: true,
                                    balanced: undefined,
                                    longestSuitMin: undefined,
                                    longestSuitMax: undefined,
                                    fourCardMajor: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opener!.hcpMin = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>HCP Max: <input type="number" min="0" max="40" value={category.criteria.opener?.hcpMax || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opener) {
                                  newCategories[index].criteria.opener = {
                                    hcpMin: undefined,
                                    hcpMax: undefined,
                                    openingBids: [],
                                    allowRuleOf20: true,
                                    balanced: undefined,
                                    longestSuitMin: undefined,
                                    longestSuitMax: undefined,
                                    fourCardMajor: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opener!.hcpMax = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Longest Suit Min: <input type="number" min="4" max="13" value={category.criteria.opener?.longestSuitMin || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opener) {
                                  newCategories[index].criteria.opener = {
                                    hcpMin: undefined,
                                    hcpMax: undefined,
                                    openingBids: [],
                                    allowRuleOf20: true,
                                    balanced: undefined,
                                    longestSuitMin: undefined,
                                    longestSuitMax: undefined,
                                    fourCardMajor: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opener!.longestSuitMin = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Longest Suit Max: <input type="number" min="4" max="13" value={category.criteria.opener?.longestSuitMax || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opener) {
                                  newCategories[index].criteria.opener = {
                                    hcpMin: undefined,
                                    hcpMax: undefined,
                                    openingBids: [],
                                    allowRuleOf20: true,
                                    balanced: undefined,
                                    longestSuitMin: undefined,
                                    longestSuitMax: undefined,
                                    fourCardMajor: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opener!.longestSuitMax = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Balance: 
                                <select value={category.criteria.opener?.balanced || ''} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.opener) {
                                    newCategories[index].criteria.opener = {
                                      hcpMin: undefined,
                                      hcpMax: undefined,
                                      openingBids: [],
                                      allowRuleOf20: true,
                                      balanced: undefined,
                                      longestSuitMin: undefined,
                                      longestSuitMax: undefined,
                                      fourCardMajor: undefined
                                    };
                                  }
                                  const value = e.target.value === '' ? undefined : e.target.value as any;
                                  newCategories[index].criteria.opener!.balanced = value;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="">Don't Care</option>
                                  <option value="BalancedNoFiveCardMajor">Balanced (no 5-card major)</option>
                                  <option value="BalancedAllowFiveCardMajor">Balanced (allow 5-card major)</option>
                                  <option value="NotBalanced">Not Balanced</option>
                                </select>
                              </label>
                              <label>4-Card Major: 
                                <select value={category.criteria.opener?.fourCardMajor || ''} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.opener) {
                                    newCategories[index].criteria.opener = {
                                      hcpMin: undefined,
                                      hcpMax: undefined,
                                      openingBids: [],
                                      allowRuleOf20: true,
                                      balanced: undefined,
                                      longestSuitMin: undefined,
                                      longestSuitMax: undefined,
                                      fourCardMajor: undefined
                                    };
                                  }
                                  const value = e.target.value === '' ? undefined : e.target.value as any;
                                  newCategories[index].criteria.opener!.fourCardMajor = value;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="">Don't Care</option>
                                  <option value="Required">Required</option>
                                  <option value="NotAllowed">Not Allowed</option>
                                  <option value="AtLeastFive">At Least 5</option>
                                </select>
                              </label>
                            </div>
                            <div className="criteria-row">
                              <label>Opening Bids: 
                                <select multiple value={category.criteria.opener?.openingBids || ['DontCare']} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.opener) {
                                    newCategories[index].criteria.opener = {
                                      hcpMin: undefined,
                                      hcpMax: undefined,
                                      openingBids: ['DontCare'],
                                      allowRuleOf20: true,
                                      balanced: undefined,
                                      longestSuitMin: undefined,
                                      longestSuitMax: undefined,
                                      fourCardMajor: undefined
                                    };
                                  }
                                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                  
                                  // If "DontCare" is selected, clear all other options
                                  if (selectedOptions.includes('DontCare')) {
                                    newCategories[index].criteria.opener!.openingBids = ['DontCare'];
                                  } else {
                                    // If other options are selected, remove "DontCare"
                                    newCategories[index].criteria.opener!.openingBids = selectedOptions.filter(option => option !== 'DontCare');
                                  }
                                  
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="DontCare">Don't Care</option>
                                  <option value="Pass">Pass</option>
                                  <option value="1C">1♣</option>
                                  <option value="1D">1♦</option>
                                  <option value="1H">1♥</option>
                                  <option value="1S">1♠</option>
                                  <option value="1NT">1NT</option>
                                  <option value="2C">2♣</option>
                                  <option value="2D">2♦</option>
                                  <option value="2H">2♥</option>
                                  <option value="2S">2♠</option>
                                  <option value="2NT">2NT</option>
                                  <option value="3C">3♣</option>
                                  <option value="3D">3♦</option>
                                  <option value="3H">3♥</option>
                                  <option value="3S">3♠</option>
                                  <option value="4C">4♣</option>
                                  <option value="4D">4♦</option>
                                  <option value="4H">4♥</option>
                                  <option value="4S">4♠</option>
                                </select>
                              </label>
                            </div>
                            <div className="criteria-row">
                              <label>
                                Allow Rule of 20 Opening:
                                <input
                                  type="checkbox"
                                  checked={category.criteria.opener?.allowRuleOf20 ?? true}
                                  onChange={(e) => {
                                    const newCategories = [...configuration.categories];
                                    if (!newCategories[index].criteria.opener) {
                                      newCategories[index].criteria.opener = {
                                        hcpMin: undefined,
                                        hcpMax: undefined,
                                        openingBids: [],
                                        allowRuleOf20: true,
                                        balanced: undefined,
                                        longestSuitMin: undefined,
                                        longestSuitMax: undefined,
                                        fourCardMajor: undefined
                                      };
                                    }
                                    newCategories[index].criteria.opener!.allowRuleOf20 = e.target.checked;
                                    setConfiguration({ ...configuration, categories: newCategories });
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Responder Criteria Tab */}
                        {activeCriteriaTabs[index] === 'responder' && (
                          <div className="criteria-tab-content">
                            <h4>Responder Requirements</h4>
                            <div className="criteria-row">
                              <label>HCP Min: <input type="number" min="0" max="40" value={category.criteria.responder?.hcpMin || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.responder) {
                                  newCategories[index].criteria.responder = {
                                    hcpMin: undefined,
                                    hcpMax: undefined,
                                    fourCardMajor: undefined,
                                    balanced: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.responder!.hcpMin = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>HCP Max: <input type="number" min="0" max="40" value={category.criteria.responder?.hcpMax || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.responder) {
                                  newCategories[index].criteria.responder = {
                                    hcpMin: undefined,
                                    hcpMax: undefined,
                                    fourCardMajor: undefined,
                                    balanced: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.responder!.hcpMax = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Balance: 
                                <select value={category.criteria.responder?.balanced || ''} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.responder) {
                                    newCategories[index].criteria.responder = {
                                      hcpMin: undefined,
                                      hcpMax: undefined,
                                      fourCardMajor: undefined,
                                      balanced: undefined
                                    };
                                  }
                                  const value = e.target.value === '' ? undefined : e.target.value as any;
                                  newCategories[index].criteria.responder!.balanced = value;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="">Don't Care</option>
                                  <option value="BalancedNoFiveCardMajor">Balanced (no 5-card major)</option>
                                  <option value="BalancedAllowFiveCardMajor">Balanced (allow 5-card major)</option>
                                  <option value="NotBalanced">Not Balanced</option>
                                </select>
                              </label>
                              <label>4-Card Major: 
                                <select value={category.criteria.responder?.fourCardMajor || ''} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.responder) {
                                    newCategories[index].criteria.responder = {
                                      hcpMin: undefined,
                                      hcpMax: undefined,
                                      fourCardMajor: undefined,
                                      balanced: undefined
                                    };
                                  }
                                  const value = e.target.value === '' ? undefined : e.target.value as any;
                                  newCategories[index].criteria.responder!.fourCardMajor = value;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="">Don't Care</option>
                                  <option value="Required">Required</option>
                                  <option value="NotAllowed">Not Allowed</option>
                                  <option value="AtLeastFive">At Least 5</option>
                                </select>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Opening Partnership Criteria Tab */}
                        {activeCriteriaTabs[index] === 'openingPartnership' && (
                          <div className="criteria-tab-content">
                            <h4>Opening Partnership Requirements</h4>
                            <div className="criteria-row">
                              <label>Combined HCP Min: <input type="number" min="0" max="80" value={category.criteria.openingPartnership?.totalHcpMin || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.openingPartnership!.totalHcpMin = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Combined HCP Max: <input type="number" min="0" max="80" value={category.criteria.openingPartnership?.totalHcpMax || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.openingPartnership!.totalHcpMax = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Min Aces: <input type="number" min="0" max="8" value={category.criteria.openingPartnership?.minAcesCount || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.openingPartnership!.minAcesCount = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Min Kings: <input type="number" min="0" max="8" value={category.criteria.openingPartnership?.minKingsCount || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.openingPartnership!.minKingsCount = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Total Voids: <input type="number" min="0" max="8" value={category.criteria.openingPartnership?.totalVoids || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.openingPartnership!.totalVoids = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Total Singletons: <input type="number" min="0" max="8" value={category.criteria.openingPartnership?.totalSingletons || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.openingPartnership!.totalSingletons = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Fit: 
                                <select value={category.criteria.openingPartnership?.fit || ''} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.openingPartnership) {
                                    newCategories[index].criteria.openingPartnership = {
                                      totalHcpMin: undefined,
                                      totalHcpMax: undefined,
                                      minAcesCount: undefined,
                                      minKingsCount: undefined,
                                      totalVoids: undefined,
                                      totalSingletons: undefined,
                                      fit: undefined,
                                      fitLength: undefined
                                    };
                                  }
                                  const value = e.target.value === '' ? undefined : e.target.value as any;
                                  newCategories[index].criteria.openingPartnership!.fit = value;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="">Don't Care</option>
                                  <option value="Any">Any</option>
                                  <option value="Major">Major</option>
                                  <option value="FourCardMajor">4/4 Major</option>
                                  <option value="Minor">Minor</option>
                                  <option value="None">None</option>
                                </select>
                              </label>
                              <label>Fit Length: <input type="number" min="8" max="13" value={category.criteria.openingPartnership?.fitLength || 8} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.openingPartnership) {
                                  newCategories[index].criteria.openingPartnership = {
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = parseInt(e.target.value) || 8;
                                newCategories[index].criteria.openingPartnership!.fitLength = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                          </div>
                        )}

                        {/* Opponent Partnership Criteria Tab */}
                        {activeCriteriaTabs[index] === 'opponentPartnership' && (
                          <div className="criteria-tab-content">
                            <h4>Opponent Partnership Requirements</h4>
                            <div className="criteria-row">
                              <label>Combined HCP Min: <input type="number" min="0" max="80" value={category.criteria.opponentPartnership?.totalHcpMin || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.totalHcpMin = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Combined HCP Max: <input type="number" min="0" max="80" value={category.criteria.opponentPartnership?.totalHcpMax || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.totalHcpMax = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>

                            <div className="criteria-row">
                              <label>Min Aces: <input type="number" min="0" max="8" value={category.criteria.opponentPartnership?.minAcesCount || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.minAcesCount = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Min Kings: <input type="number" min="0" max="8" value={category.criteria.opponentPartnership?.minKingsCount || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.minKingsCount = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Total Voids: <input type="number" min="0" max="8" value={category.criteria.opponentPartnership?.totalVoids || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.totalVoids = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>Total Singletons: <input type="number" min="0" max="8" value={category.criteria.opponentPartnership?.totalSingletons || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.totalSingletons = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Fit: 
                                <select value={category.criteria.opponentPartnership?.fit || ''} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.opponentPartnership) {
                                    newCategories[index].criteria.opponentPartnership = {
                                      lhoOvercall: [],
                                      lhoHcpMin: undefined,
                                      lhoHcpMax: undefined,
                                      totalHcpMin: undefined,
                                      totalHcpMax: undefined,
                                      minAcesCount: undefined,
                                      minKingsCount: undefined,
                                      totalVoids: undefined,
                                      totalSingletons: undefined,
                                      fit: undefined,
                                      fitLength: undefined
                                    };
                                  }
                                  const value = e.target.value === '' ? undefined : e.target.value as any;
                                  newCategories[index].criteria.opponentPartnership!.fit = value;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="">Don't Care</option>
                                  <option value="Any">Any</option>
                                  <option value="Major">Major</option>
                                  <option value="FourCardMajor">4/4 Major</option>
                                  <option value="Minor">Minor</option>
                                  <option value="None">None</option>
                                </select>
                              </label>
                              <label>Fit Length: <input type="number" min="8" max="13" value={category.criteria.opponentPartnership?.fitLength || 8} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = parseInt(e.target.value) || 8;
                                newCategories[index].criteria.opponentPartnership!.fitLength = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                          </div>
                        )}

                                              {/* LHO Overcall Criteria Tab */}
                                              {activeCriteriaTabs[index] === 'lhoOvercall' && (
                          <div className="criteria-tab-content">
                            <h4>Left Hand Opponent Requirements</h4>
                            <div className="criteria-row">
                              <label>LHO HCP Min: <input type="number" min="0" max="40" value={category.criteria.opponentPartnership?.lhoHcpMin || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.lhoHcpMin = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                              <label>LHO HCP Max: <input type="number" min="0" max="40" value={category.criteria.opponentPartnership?.lhoHcpMax || ''} onChange={(e) => {
                                const newCategories = [...configuration.categories];
                                if (!newCategories[index].criteria.opponentPartnership) {
                                  newCategories[index].criteria.opponentPartnership = {
                                    lhoOvercall: [],
                                    lhoHcpMin: undefined,
                                    lhoHcpMax: undefined,
                                    totalHcpMin: undefined,
                                    totalHcpMax: undefined,
                                    minAcesCount: undefined,
                                    minKingsCount: undefined,
                                    totalVoids: undefined,
                                    totalSingletons: undefined,
                                    fit: undefined,
                                    fitLength: undefined
                                  };
                                }
                                const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                newCategories[index].criteria.opponentPartnership!.lhoHcpMax = value;
                                setConfiguration({ ...configuration, categories: newCategories });
                              }} /></label>
                            </div>
                            <div className="criteria-row">
                              <label>Possible Overcalls: 
                                <select multiple value={category.criteria.opponentPartnership?.lhoOvercall || []} onChange={(e) => {
                                  const newCategories = [...configuration.categories];
                                  if (!newCategories[index].criteria.opponentPartnership) {
                                    newCategories[index].criteria.opponentPartnership = {
                                      lhoOvercall: [],
                                      lhoHcpMin: undefined,
                                      lhoHcpMax: undefined,
                                      totalHcpMin: undefined,
                                      totalHcpMax: undefined,
                                      minAcesCount: undefined,
                                      minKingsCount: undefined,
                                      totalVoids: undefined,
                                      totalSingletons: undefined,
                                      fit: undefined,
                                      fitLength: undefined
                                    };
                                  }
                                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                  newCategories[index].criteria.opponentPartnership!.lhoOvercall = selectedOptions;
                                  setConfiguration({ ...configuration, categories: newCategories });
                                }}>
                                  <option value="Pass">Pass</option>
                                  <option value="Double">Double</option>
                                  <option value="1C">1♣</option>
                                  <option value="1D">1♦</option>
                                  <option value="1H">1♥</option>
                                  <option value="1S">1♠</option>
                                  <option value="1NT">1NT</option>
                                  <option value="2C">2♣</option>
                                  <option value="2D">2♦</option>
                                  <option value="2H">2♥</option>
                                  <option value="2S">2♠</option>
                                </select>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'deals' && (
          <div className="deals-panel">
            <div className="deals-header">
              <h2>Generated Deals</h2>
              <div className="deals-controls">
                <div className="view-toggle">
                  <button 
                    className={viewMode === 'grid' ? 'active' : ''} 
                    onClick={() => setViewMode('grid')}
                  >
                    Grid View
                  </button>
                  <button 
                    className={viewMode === 'list' ? 'active' : ''} 
                    onClick={() => setViewMode('list')}
                  >
                    List View
                  </button>
                </div>
                <div className="export-buttons">
                  <button onClick={() => handleExport('PBN')}>Export PBN</button>
                  <button onClick={() => handleExport('BRI')}>Export BRI</button>
                  <button onClick={() => handleExport('HTML')}>Export HTML</button>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
                <button onClick={() => setError(null)}>×</button>
              </div>
            )}

            {viewMode === 'grid' && (
              <div className="deals-list">
                {deals.map((deal, index) => (
                  <div key={deal.dealNumber} className="deal-item">
                    <button
                      type="button"
                      className="redeal-btn"
                      title="Re-deal this board using its original category"
                      disabled={redealingDealNumber !== null || loading}
                      onClick={() => handleRedeal(index)}
                    >
                      {redealingDealNumber === deal.dealNumber ? 'Re-dealing…' : 'Re-deal'}
                    </button>
                    <div className="deal-header">
                      <span>Board {deal.dealNumber}</span>
                      <span>Dealer: {deal.dealer}</span>
                      <span>Vuln: {deal.vulnerability}</span>
                      <span>Opening: {deal.openingHand}</span>
                      <span className="category-full-row">Category: {deal.category}</span>
                    </div>
                    <div className="deal-hands">
                      <div className="hand north">
                        <strong>North ({deal.north.hcp})</strong>
                        {formatHand(deal.north.cards)}
                      </div>
                      <div className="rotation-actions">
                        <button onClick={() => handleDealRotation(index, 'clockwise')}>↻</button>
                        <button onClick={() => handleDealRotation(index, 'counterclockwise')}>↺</button>
                      </div>
                      <div className="move-actions">
                        {index > 0 && (
                          <button onClick={() => handleDealReorder(index, index - 1)}>↑</button>
                        )}
                        {index < deals.length - 1 && (
                          <button onClick={() => handleDealReorder(index, index + 1)}>↓</button>
                        )}
                      </div>
                      <div className="hand west">
                        <strong>West ({deal.west.hcp})</strong>
                        {formatHand(deal.west.cards)}
                      </div>
                      <div className="hand east">
                        <strong>East ({deal.east.hcp})</strong>
                        {formatHand(deal.east.cards)}
                      </div>
                      <div className="hand south">
                        <strong>South ({deal.south.hcp})</strong>
                        {formatHand(deal.south.cards)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'list' && (
              <div className="deals-list-view">
                <div className="deals-list-container">
                  {deals.map((deal, index) => (
                    <div 
                      key={deal.dealNumber} 
                      className={`deal-list-item ${selectedDeal?.dealNumber === deal.dealNumber ? 'selected' : ''}`}
                      onClick={() => setSelectedDeal(deal)}
                    >
                      <div className="deal-list-header">
                        <span className="deal-number">Board {deal.dealNumber}</span>
                        <span className="deal-info">Dealer: {deal.dealer} | Vuln: {deal.vulnerability}</span>
                        <span className="deal-info">Opening: {deal.openingHand} | Category: {deal.category}</span>
                      </div>
                      <div className="deal-list-hands">
                        <div className="hand-summary">
                          <span>N: {deal.north.hcp} HCP</span>
                          <span>S: {deal.south.hcp} HCP</span>
                          <span>E: {deal.east.hcp} HCP</span>
                          <span>W: {deal.west.hcp} HCP</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {selectedDeal && (
                  <div className="selected-deal-display">
                    <div className="bridge-table">
                      <button
                        type="button"
                        className="redeal-btn"
                        title="Re-deal this board using its original category"
                        disabled={redealingDealNumber !== null || loading}
                        onClick={() => {
                          const index = deals.findIndex((d) => d.dealNumber === selectedDeal.dealNumber);
                          if (index >= 0) {
                            handleRedeal(index);
                          }
                        }}
                      >
                        {redealingDealNumber === selectedDeal.dealNumber ? 'Re-dealing…' : 'Re-deal'}
                      </button>
                      <div className="table-header">
                        <h3>Board {selectedDeal.dealNumber}</h3>
                        <div className="table-info">
                          <span>Dealer: {selectedDeal.dealer}</span>
                          <span>Vuln: {selectedDeal.vulnerability}</span>
                          <span>Opening: {selectedDeal.openingHand}</span>
                          <span>Category: {selectedDeal.category}</span>
                        </div>
                      </div>
                      <div className="table-layout">
                        <div className="table-position north">
                          <div className="position-label">North ({selectedDeal.north.hcp})</div>
                          <div className="hand-cards">{formatHand(selectedDeal.north.cards)}</div>
                        </div>
                        <div className="table-position west">
                          <div className="position-label">West ({selectedDeal.west.hcp})</div>
                          <div className="hand-cards">{formatHand(selectedDeal.west.cards)}</div>
                        </div>
                        <div className="table-position east">
                          <div className="position-label">East ({selectedDeal.east.hcp})</div>
                          <div className="hand-cards">{formatHand(selectedDeal.east.cards)}</div>
                        </div>
                        <div className="table-position south">
                          <div className="position-label">South ({selectedDeal.south.hcp})</div>
                          <div className="hand-cards">{formatHand(selectedDeal.south.cards)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {!selectedDeal && (
                  <div className="selected-deal-display">
                    <div className="no-deal-selected">
                      <h3>Select a deal from the list to view</h3>
                      <p>Click on any deal in the list to display it as a bridge table</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && stats && (
          <div className="stats-panel">
            <h2>Statistics</h2>
            
            <div className="stats-section">
              <h3>Partnership Statistics</h3>
              <p>NS Average HCP: {stats.partnershipStats.nsAverageHcp}</p>
              <p>EW Average HCP: {stats.partnershipStats.ewAverageHcp}</p>
            </div>

            <div className="stats-section">
              <h3>Individual Statistics</h3>
              <p>North Average HCP: {stats.individualStats.northAverageHcp}</p>
              <p>South Average HCP: {stats.individualStats.southAverageHcp}</p>
              <p>East Average HCP: {stats.individualStats.eastAverageHcp}</p>
              <p>West Average HCP: {stats.individualStats.westAverageHcp}</p>
            </div>

            <div className="stats-section">
              <h3>Opening Hand Distribution</h3>
              {Object.entries(stats.openingHandStats.openingHandPercentages).map(([direction, percentage]) => (
                <p key={direction}>
                  {direction}: {stats.openingHandStats.openingHandCounts[direction]} deals ({percentage.toFixed(1)}%)
                </p>
              ))}
            </div>

            {generationStats && (
              <div className="stats-section">
                <h3>Generation Statistics</h3>
                <p>Total Attempts: {generationStats.totalAttempts.toLocaleString()}</p>
                <p>Average Attempts per Deal: {(generationStats.totalAttempts / deals.length).toFixed(1)}</p>
                
                <h4>Attempts per Category:</h4>
                {Object.entries(generationStats.categoryAttempts).map(([category, attempts]) => {
                  const categoryDeals = deals.filter(d => d.category === category).length;
                  const avgAttempts = categoryDeals > 0 ? attempts / categoryDeals : 0;
                  return (
                    <p key={category}>
                      {category}: {attempts.toLocaleString()} attempts ({avgAttempts.toFixed(1)} avg per deal)
                    </p>
                  );
                })}
                
                {generationStats.failedCategories.length > 0 && (
                  <div className="error-section">
                    <h4>Failed Categories:</h4>
                    {generationStats.failedCategories.map((category, index) => (
                      <p key={index} className="error-message">
                        {category}: Failed to generate deal after 100,000 attempts
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'help' && (
          <div className="help-panel">
            <h2>Help & Documentation</h2>
            
            <div className="help-section">
              <h3>What is Bridge Deal Generator?</h3>
              <p>
                Bridge Deal Generator helps you create custom bridge deals based on your specifications. 
                It generates hands that meet specific criteria like high card points, suit distributions, and bidding requirements.
                At the moment it does not support Double Dummy Analysis, but it is on the roadmap.
              </p>
            </div>

            <div className="help-section">
              <h3>How to Use the Application</h3>
              
              <h4>1. Configuration Tab</h4>
              <ul>
                <li><strong>Total Deals:</strong> Set how many deals you want to generate, the more you generate the longer it will take to generate.</li>
                <li><strong>Bidding System:</strong> Choose your preferred bidding system, this is only used in the opening hand opening bid check</li>
                <li><strong>Show Samples:</strong> Browse built-in sample configurations that demonstrate common setups. Loading a sample replaces your current config; use Import/Export for your own saved files.</li>
                <li><strong>Categories:</strong> Define different types of hands with specific criteria, you can add as many categories as you want. This is useful for having a range of hands that meet different criteria.</li>
              </ul>

              <h4>2. Creating Categories</h4>
              <ul>
                <li><strong>Name:</strong> Give your category a descriptive name</li>
                <li><strong>Weight:</strong> Higher weight = more frequent appearance in generated deals</li>
                <li><strong>Set Dealer:</strong> Control opening hand positioning relative to the dealer (None=no constraint, Exact=is the dealer, Close=is the dealer or one hand after the dealer)</li>
                <li><strong>Set opening direction:</strong> When Set Dealer is None, rotate the deal so the opening hand sits at a chosen seat (Any, N or S, E or W, or a specific direction). Shown disabled when Exact or Close is selected.</li>
              </ul>

              <h4>3. Setting Criteria</h4>
              <p>Each category has 5 different criteria tabs, you only need to fill in the ones you want to use:</p>
              <ul>
                <li><strong>Opener:</strong> Requirements for the opening hand (e.g., minimum HCP, suit lengths)</li>
                <li><strong>Responder:</strong> Requirements for the responding hand</li>
                <li><strong>Opening Partnership:</strong> Combined requirements for both hands in the opening partnership</li>
                <li><strong>Opponent Partnership:</strong> Combined requirements for both hands in the opponent partnership</li>
                <li><strong>LHO Overcall:</strong> The overcall for the Left Hand Opponent</li>
              </ul>

              <h4>4. Generating Deals</h4>
              <ul>
                <li>Click "Generate Deals" to create hands based on your configuration</li>
                <li>The system will attempt to generate deals that meet all criteria</li>
                <li>Generated deals appear in the Deals tab</li>
              </ul>

              <h4>5. Viewing and Managing Deals</h4>
              <ul>
                <li><strong>Grid View:</strong> See multiple deals at once in a card layout</li>
                <li><strong>List View:</strong> Compact list with deal summaries</li>
                <li><strong>Actions:</strong> Rotate hands clockwise/counterclockwise, move deals up/down, or Re-deal a board to generate a new deal that still matches its original category</li>
              </ul>

              <h4>6. Exporting Deals</h4>
              <ul>
                <li><strong>PBN:</strong> Portable Bridge Notation format for bridge software</li>
                <li><strong>BRI:</strong> Another format required for some bridge software</li>
                <li><strong>HTML:</strong> Web-friendly format for sharing or printing</li>
              </ul>
            </div>
            <div className="help-section">
              <h3>How it works</h3>
              <ul>
                <li>The application generates a random deal, it chooses a random hand position for the opening hand</li>
                <li>It then checks if the deal meets the criteria for the category it is in</li>
                <li>If the deal does not meet the criteria, it checks the remaining hand positions of that deal as the opening hand</li>
                <li>If they do not meet the criteria either the deal is discarded and the application tries again</li>
                <li>This process is repeated until application has found the right number of deals</li>
              </ul>
            </div>
            <div className="help-section">
              <h3>Tips for Best Results</h3>
              <ul>
                <li>Start with simple criteria and gradually add complexity</li>
                <li>Use reasonable HCP ranges (e.g., 12-20 for opening hands)</li>
                <li>Balance category weights to get the mix of hands you want</li>
                <li>Save your configuration for future use</li>
                <li>Export deals in your preferred format for use in bridge software</li>
                <li>The "opening hand" in the application is the hand that was found to fit the criteria for the category it is in.  It may not be the dealer.  If you want it to be the dealer, you can set the "Set Dealer" to "Exact"</li>
                </ul>
            </div>

            <div className="help-section">
              <h3>Understanding the Statistics</h3>
              <ul>
                <li><strong>Partnership Statistics:</strong> Average HCP for NS and EW partnerships</li>
                <li><strong>Individual Statistics:</strong> Average HCP for each position</li>
                <li><strong>Opening Hand Distribution:</strong> How often each position opens</li>
                <li><strong>Generation Statistics:</strong> Performance metrics for deal generation</li>
              </ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const formatHand = (cards: any[]): JSX.Element => {
  const suits = ['S', 'H', 'C', 'D']; // Spades, Hearts, Clubs, Diamonds
  const suitSymbols = ['♠', '♥', '♣', '♦'];
  const redSuits = ['H', 'D']; // Hearts and Diamonds
  
  return (
    <div className="hand-vertical">
      {suits.map((suit, index) => {
        const suitCards = cards
          .filter(card => card.suit === suit)
          .sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank))
          .map(card => card.rank)
          .join('');
        
        return (
          <div 
            key={suit} 
            className={`suit-line ${redSuits.includes(suit) ? 'red-suit' : 'black-suit'}`}
          >
            <span className="suit-symbol">{suitSymbols[index]}</span>
            <span className="suit-cards">{suitCards}</span>
          </div>
        );
      })}
    </div>
  );
};

const getRankValue = (rank: string): number => {
  const rankValues: { [key: string]: number } = {
    'A': 14, 'K': 13, 'Q': 12, 'J': 11, 'T': 10,
    '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2
  };
  return rankValues[rank] || 0;
};

export default App; 